import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectProfile } from 'screens/Login/selectors';
import { filterOption } from 'utils/libs';
import { Span } from 'components';
import Select from 'components/Select';
import {
	filterReportOptionIds,
	getDefaultReportProjects,
	getLegacyReportFilterKeys,
	isCardexProjectReport,
	isReportProjectBoundToFeed,
	resetReportScopeValues,
} from './reportContract';
import { getLegacyReportFilterOptions } from './legacyReportOptions';

const labels = {
	projectsParam: 'Proyectos',
	entityParam: 'Contratistas',
	warehouseParam: 'Almacenes',
	itemParam: 'Artículos',
};

export const LegacyReportFilterFields = ({
	filterKeys,
	reportParams,
	values,
	setValues,
	options = {},
	loading = {},
	errors = {},
	projectBoundToFeed = false,
}) =>
	filterKeys.map(param => {
		const metadata = reportParams[param];
		const multiple =
			param === 'projectsParam' || param === 'itemParam' || !!metadata.multiple;
		const currentValue =
			param === 'projectsParam' && projectBoundToFeed
				? [Number(values.selectedProjectId)]
				: param === 'projectsParam' && values[param] === undefined
				? getDefaultReportProjects(reportParams, values.selectedProjectId)
				: values[param];
		const displayedValue =
			!multiple && Array.isArray(currentValue) ? currentValue[0] : currentValue;

		return (
			<React.Fragment key={param}>
				<Select
					name={param}
					placeholder={`${labels[param]}${metadata.required ? '*' : ''}`}
					mode={multiple ? 'tags' : undefined}
					margin='20px 0 0 0'
					width='100%'
					showSearch
					loading={!!loading[param]}
					disabled={
						!!loading[param] ||
						!!errors[param] ||
						(param === 'projectsParam' && projectBoundToFeed)
					}
					value={displayedValue}
					filterOption={filterOption}
					onChange={value => {
						const filtered = multiple
							? filterReportOptionIds(value, options[param])
							: value;
						setValues(prev => ({ ...prev, [param]: filtered }));
					}}
				>
					{(options[param] || []).map(option => (
						<Select.Option key={option.id} value={option.id}>
							{option.name}
						</Select.Option>
					))}
				</Select>
				{param === 'projectsParam' && projectBoundToFeed && (
					<Span>
						Cambie el proyecto en el menú para actualizar los artículos.
					</Span>
				)}
				{errors[param] && (
					<Span>No se pudo cargar {labels[param].toLowerCase()}.</Span>
				)}
			</React.Fragment>
		);
	});

const LegacyReportFilters = ({
	components,
	reportParams,
	values,
	setValues,
	reportId,
}) => {
	const profile = useSelector(selectProfile);
	const filterKeys = useMemo(
		() => getLegacyReportFilterKeys(components, reportParams),
		[components, reportParams]
	);
	const projectBoundToFeed =
		!isCardexProjectReport(reportId, components, reportParams) &&
		isReportProjectBoundToFeed(
			components,
			reportParams,
			values.selectedProjectId
		);
	const scopeKey = JSON.stringify([
		reportId,
		values.selectedModuleItemId,
		values.selectedProjectId,
		values.selectedOrderTargetFilterId,
	]);
	const previousScope = useRef(scopeKey);
	const [state, setState] = useState({ options: {}, loading: {}, errors: {} });

	useEffect(() => {
		if (previousScope.current !== scopeKey) {
			setValues(prev =>
				resetReportScopeValues({
					values: prev,
					components,
					reportParams,
					selectedProjectId: values.selectedProjectId,
					reportId,
				})
			);
			previousScope.current = scopeKey;
		}
	}, [scopeKey, components, reportParams, values.selectedProjectId, setValues]);

	useEffect(() => {
		let active = true;
		setState({
			options: {},
			loading: Object.fromEntries(filterKeys.map(param => [param, true])),
			errors: {},
		});
		for (const param of filterKeys) {
			if (!profile && (param === 'entityParam' || param === 'warehouseParam'))
				continue;
			getLegacyReportFilterOptions(param, {
				moduleItemId: values.selectedModuleItemId,
				profile,
			})
				.then(options => {
					if (!active) return;
					setState(prev => ({
						...prev,
						options: { ...prev.options, [param]: options },
						loading: { ...prev.loading, [param]: false },
					}));
				})
				.catch(() => {
					if (!active) return;
					setState(prev => ({
						...prev,
						loading: { ...prev.loading, [param]: false },
						errors: { ...prev.errors, [param]: true },
					}));
				});
		}
		return () => {
			active = false;
		};
	}, [filterKeys, scopeKey, profile, values.selectedModuleItemId]);

	return (
		<LegacyReportFilterFields
			filterKeys={filterKeys}
			reportParams={reportParams}
			values={values}
			setValues={setValues}
			projectBoundToFeed={projectBoundToFeed}
			{...state}
		/>
	);
};

export default LegacyReportFilters;
